import { Component, computed, inject, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { FolderMedicalReportPageStore } from './folder-medical-report-page.store';
import { TableColumn, TableColumnType } from '../../../../shared/components/kit/table/table.types';
import { timeField, userChipField } from '../../../../shared/components/kit/table/table.fields';
import { UserChipRenderTypeEnum } from '../../../../../components/kit/user-chip/user-chip.component.types';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import {
    FolderMedicalReportGridItemObject,
} from '@libs/types/src/services/object-type/folder/folder-medical-report.object';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { translate } from '../../../../shared/shared.global-service';
import {
    MedicalReportCreateDialogComponent,
} from '../../../components/person/medical-report-create-dialog/medical-report-create-dialog.component';
import { ActivatedRoute } from '@angular/router';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { contentGlobalSuccess } from '../../../content.error';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';

@Component({
    selector: 'app-folder-medical-report-page',
    templateUrl: './folder-medical-report-page.component.html',
    styleUrl: './folder-medical-report-page.component.scss',
    providers: [FolderMedicalReportPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TableComponent,
    ],
})
export class FolderMedicalReportPageComponent extends SubscriptionComponent {
    public readonly store = inject(FolderMedicalReportPageStore);
    public readonly dialogService = inject(BaseDialogService);
    public readonly confirm = inject(ConfirmBuilder);
    public readonly route = inject(ActivatedRoute);
    protected readonly folderStore = inject(FolderGlobalStore);

    protected recordsTable = viewChild<TableComponent>('folderMedicalReports');

    protected prefix = 'folder.medicalReports.';

    protected columns: TableColumn[] = [
        {
            name: 'date',
            type: TableColumnType.Date,
            dataKey: 'createdAt',
        },
        timeField('createdAt'),
        {
            name: 'content',
            type: TableColumnType.String50,
        },
        userChipField('worker', UserChipRenderTypeEnum.User),
    ];

    protected extraFilters = computed(() => {
        return [
            [
                {
                    value: [this.folderStore.folderId().toString()],
                    column: 'folderId',
                    operator: 'EQ',
                },
            ],
        ];
    });

    private dateFrom: Date | null = null;
    private dateTo: Date | null = null;

    constructor() {
        super();

        this.subs.push(
            this.route.queryParams.subscribe((params) => {
                if (!params.dateFrom && !params.dateTo) {
                    this.setLast30Days();
                } else {
                    this.dateFrom = params.dateFrom ? new Date(params.dateFrom) : null;
                    this.dateTo = params.dateTo ? new Date(params.dateTo) : null;
                }

                if (params.open === 'true') {
                    this.openDialog();
                }
            }),
        );
    }

    protected delete(input: any): void {
        this.confirm.confirm(
            'common.delete',
            null,
            () => {
                this.store.api.deleteFolderMedicalReport({ id: input.item.id }).then(() => {
                    contentGlobalSuccess.next('common.deleted');
                    this.recordsTable()?.reload();
                });
            },
            null,
            'pi-trash',
            input.event.target,
        );
    }

    protected openDialog(item: FolderMedicalReportGridItemObject | null = null): void {
        const ref = this.dialogService.open(MedicalReportCreateDialogComponent, {
            header: translate.instant(`${this.prefix}createDialog.title`),
            styleClass: 'p-dialog-width-50',
            focusOnShow: false,
            data: { item, dateFrom: this.dateFrom, dateTo: this.dateTo },
        });

        ref?.onClose.subscribe((res) => {
            if (res && res.reload) {
                this.recordsTable()?.reload();
            }
        });
    }

    private setLast30Days() {
        const currentDate = new Date();
        const dateFrom = new Date();
        dateFrom.setDate(currentDate.getDate() - 30);

        this.dateFrom = dateFrom;
        this.dateTo = currentDate;
    }
}
